<template>
  <div class="group">
    <t-label v-if="label" :opened="opened" :changed="changed" :tooltip="resetTooltip" @reset="emit('reset')">{{ label }}</t-label>
    <div
      ref="outsideDrop"
      class="t-dropdown dark:text-gray-5 relative rounded-md border border-solid border-gray-600 bg-gray-600 font-inter text-xs font-medium text-gray-50 transition duration-300 group-focus-within:text-gray-10 group-focus-within:outline group-focus-within:outline-2 group-focus-within:outline-accent-1 group-hover:border-gray-400"
      :class="{
        '!rounded-b-none !border-transparent !text-gray-10 outline outline-2 outline-accent-1':
          opened,
        '!outline !outline-1 !outline-destructive-2 focus:!outline-destructive-2':
          error,
      }"
    >
      <button
        v-tooltip.bottom="{
          content:
            typeof selected === 'string' && selected.length > 15
              ? selected
              : '',
          delay: { show: 250 },
        }"
        class="box-border flex w-full items-center justify-between overflow-hidden rounded-md bg-gray-600 pl-2.5 pr-1 font-inter text-xs font-medium leading-[30px] text-gray-50 transition duration-300 focus:text-gray-10"
        :class="{
          '!text-gray-10': opened,
          '!pl-0': withInput,
          '!text-gray-200': disabled,
        }"
        data-testid="t-dropdown-selected-button"
        aria-haspopup="true"
        :aria-expanded="opened"
        :disabled="disabled"
        @click="opened = !opened"
      >
        <p
          v-if="!$slots['selected-content'] && selected && !withInput"
          class="truncate font-inter text-xs font-medium leading-[30px]"
          :class="{
            '!text-gray-200': disabled,
          }"
        >
          {{ selected }}
        </p>
        <p
          v-if="!$slots['selected-content'] && !selected && !withInput"
          class="truncate font-inter text-xs font-medium leading-[30px]"
          :class="{
            '!text-gray-200': disabled,
          }"
        >
          {{ data[0] }}
        </p>

        <input
          v-if="withInput && inputType === 'number'"
          v-model="localValue"
          class="w-4 border-none bg-gray-600 p-0 pl-[9px] pr-2.5 text-center font-inter text-xs font-medium leading-[30px] text-gray-50 outline-none transition duration-300 disabled:!cursor-not-allowed disabled:bg-gray-600 disabled:text-gray-200 group-focus-within:bg-gray-700 group-focus-within:text-gray-10"
          :class="{
            '!text-gray-200': disabled,
          }"
          type="number"
          :min="imin"
          :max="imax"
          :disabled="disabled"
        />

        <input
          v-if="withInput && inputType !== 'number'"
          v-model="localValue"
          class="border-none bg-gray-600 p-0 pl-[9px] pr-2.5 text-center font-inter text-xs font-medium leading-[30px] text-gray-50 outline-none transition duration-300 disabled:!cursor-not-allowed disabled:bg-gray-600 disabled:text-gray-200 group-focus-within:bg-gray-700 group-focus-within:text-gray-10"
          :class="{
            '!border-transparent !bg-gray-600 !text-gray-200': disabled,
          }"
          :type="inputType"
          :disabled="disabled"
        />

        <slot v-if="!withInput" name="selected-content" />

        <div
          class="flex h-[20px] w-[20px] cursor-pointer items-center justify-center"
          :class="{
            'pl-[10px]': !withInput,
          }"
        >
          <font-awesome-icon
            :icon="['fas', 'caret-down']"
            class="text-gray-100"
          />
        </div>
      </button>

      <PerfectScrollbar
        v-if="!$slots.content && opened"
        tag="ul"
        :style="{ height: calculatedHeight + 'px' }"
        :options="{ wheelPropagation: false }"
      >
        <li
          v-for="option in data"
          :key="getOptionKey(option)"
          @click="selectionChanged(option)"
        >
          <p
            v-tooltip="{
              content:
                getOptionLabel(option).length > 18
                  ? getOptionLabel(option)
                  : '',
              delay: { show: 250 },
            }"
            style="padding-left: 0"
            class="truncate"
          >
            {{ getOptionLabel(option) }}
          </p>
        </li>
      </PerfectScrollbar>

      <div v-show="opened" v-else>
        <slot name="content" />
      </div>
    </div>
    <div
      v-if="error && errorText !== ''"
      class="font-regular mt-1 flex items-start gap-[5px] font-inter text-xs leading-[16px] text-destructive-2"
    >
      <div class="h-5 w-5">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 2.75C13.8555 2.75 17 5.89453 17 9.75C17 13.6328 13.8555 16.75 10 16.75C6.11719 16.75 3 13.6328 3 9.75C3 5.89453 6.11719 2.75 10 2.75ZM10 15.4375C13.1172 15.4375 15.6875 12.8945 15.6875 9.75C15.6875 6.63281 13.1172 4.0625 10 4.0625C6.85547 4.0625 4.3125 6.63281 4.3125 9.75C4.3125 12.8945 6.85547 15.4375 10 15.4375ZM10 11.0625C9.61719 11.0625 9.34375 10.7891 9.34375 10.4062V6.90625C9.34375 6.55078 9.61719 6.25 10 6.25C10.3555 6.25 10.6562 6.55078 10.6562 6.90625V10.4062C10.6562 10.7891 10.3555 11.0625 10 11.0625ZM10 11.9922C10.4648 11.9922 10.8477 12.375 10.8477 12.8398C10.8477 13.3047 10.4648 13.6875 10 13.6875C9.50781 13.6875 9.125 13.3047 9.125 12.8398C9.125 12.375 9.50781 11.9922 10 11.9922Z"
            fill="currentColor"
          />
        </svg>
      </div>
      <span class="inline-flex min-h-[20px] items-center">{{ errorText }}</span>
    </div>
  </div>
</template>
<script lang="ts" setup>
import TLabel from "../TLabel/TLabel.vue";
import "vue3-perfect-scrollbar/style.css";
import { PerfectScrollbar } from "vue3-perfect-scrollbar";
import { vTooltip } from "floating-vue";
import "floating-vue/dist/style.css";
import { TDropdownOption } from "../../../types/TDropdownOptions/TDropdownOptions";
import { onClickOutside } from "@vueuse/core";
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
} from "vue";

interface Props {
  data?: Array<any>;
  selected?: string | number;
  roundedLeft?: boolean;
  roundedRight?: boolean;
  roundedFull?: boolean;
  label?: string;
  noBorderLeft?: boolean;
  withInput?: boolean;
  inputType?: string;
  imax?: number;
  imin?: number;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  changed?: boolean;
  resetTooltip?: string;
}

const props = withDefaults(defineProps<Props>(), {
  data: () => [],
  selected: "",
  roundedLeft: false,
  roundedRight: false,
  roundedFull: false,
  label: "",
  noBorderLeft: false,
  withInput: false,
  inputType: "text",
  imax: 99,
  imin: 0,
  error: false,
  errorText: "",
  disabled: false,
  changed: false,
});

const emit = defineEmits<{
  (e: "selection-changed", option: TDropdownOption): void;
  (e: "input-changed", option: string): void;
  (e: "opened"): void;
  (e: "closed"): void;
  (e: "reset"): void;
}>();

const opened = ref(false);
const outsideDrop = useTemplateRef("outsideDrop");

const localValue = computed({
  get() {
    if (props.selected !== "") {
      return props.selected;
    }

    if (props.data[0].value) {
      return props.data[0].value;
    }

    return "";
  },
  set(val) {
    emit("input-changed", val);
  },
});

const calculatedHeight = computed(() => {
  if (props.data.length > 5) {
    return 200;
  } else {
    return props.data.length * 32;
  }
});

function selectionChanged(option: TDropdownOption) {
  emit("selection-changed", option);
  opened.value = false;
}

function getOptionLabel(option: TDropdownOption | string): string {
  if (typeof option === "string") {
    return option;
  }

  return option.label || "";
}

function getOptionKey(option: TDropdownOption | string): string {
  if (typeof option === "string") {
    return option;
  }
  return option.value;
}

onClickOutside(outsideDrop, () => {
  opened.value = false;
});

defineExpose({
  opened,
});

watch(opened, (value) => {
  if (value) {
    emit("opened");
  } else {
    emit("closed");
  }
});

function closeOnEsc(e: KeyboardEvent) {
  if (e.key === "Escape") {
    opened.value = false;
    emit("closed");
  }
}

onMounted(() => {
  document.addEventListener("keydown", closeOnEsc);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", closeOnEsc);
});
</script>
<style lang="scss">
.t-dropdown {
  ul {
    position: absolute;
    z-index: 10;
    width: calc(100% + 2px);

    margin-block-start: 31px;
    margin-block-end: 0em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 0px;

    max-height: 240px;
    margin-left: -3px;
    overflow: hidden;
    padding: 0;
    top: 0;
    border: 2px solid var(--c-accent-1);
    border-top: none;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    li {
      font-size: 0.75rem;
      font-family: "Inter", sans-serif;
      font-weight: 500;
      line-height: 1;
      // @include font(
      //   --chip-font,
      //   var(--font-medium),
      //   12px,
      //   1,
      //   var(--font-family-primary)
      // );
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 4px 12px;
      line-height: 24px;
      color: var(--c-g100);
      background-color: var(--c-g600);
      transition: 0.3s;
      p {
        transition: 0.3s;
        color: var(--c-g100);
      }
      &:hover {
        transition:
          color 150ms ease-in-out,
          background-color 150ms ease-in-out,
          border 150ms ease-in-out;
        background-color: var(--c-g400);

        p {
          color: var(--c-g50);
        }
      }
    }
  }

  .ps__rail-y {
    right: 0 !important;
    left: auto !important;
  }
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
